import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { AppLayout } from "@/components/layout/AppLayout";
import { Spinner } from "@/components/ui";
import { useCurrentUser } from "@/api/lookups";

const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const MyDayPage = lazy(() => import("@/features/home/MyDayPage"));
const PipelinePage = lazy(() => import("@/features/pipeline/PipelinePage"));
const LeadDetailPage = lazy(() => import("@/features/leads/LeadDetailPage"));
const StudentsPage = lazy(() => import("@/features/students/StudentsPage"));
const StudentDetailPage = lazy(() => import("@/features/students/StudentDetailPage"));
const EnrolmentsPage = lazy(() => import("@/features/enrolments/EnrolmentsPage"));
const EnrolmentDetailPage = lazy(() => import("@/features/enrolments/EnrolmentDetailPage"));
const TasksPage = lazy(() => import("@/features/tasks/TasksPage"));
const PaymentsPage = lazy(() => import("@/features/payments/PaymentsPage"));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage"));
const AdminPage = lazy(() => import("@/features/admin/AdminPage"));

function RequireAuth() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
}

function RequireGuest() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return isAuthenticated ? <Navigate to="/" replace /> : <Outlet />;
}

function RequireRole({ allow }: { allow: "manager" | "finance" }) {
  const user = useCurrentUser();
  if (!user) return <Spinner />;
  const ok = allow === "manager" ? user.can_manage : user.can_access_finance;
  return ok ? <Outlet /> : <Navigate to="/" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<Spinner className="py-32" />}>
        <Routes>
          <Route element={<RequireGuest />}>
            <Route path="/login" element={<LoginPage />} />
          </Route>

          <Route element={<RequireAuth />}>
            <Route element={<AppLayout />}>
              <Route path="/" element={<MyDayPage />} />
              <Route path="/pipeline" element={<PipelinePage />} />
              <Route path="/leads/:id" element={<LeadDetailPage />} />
              <Route path="/students" element={<StudentsPage />} />
              <Route path="/students/:id" element={<StudentDetailPage />} />
              <Route path="/enrolments" element={<EnrolmentsPage />} />
              <Route path="/enrolments/:id" element={<EnrolmentDetailPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route element={<RequireRole allow="finance" />}>
                <Route path="/payments" element={<PaymentsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
              </Route>
              <Route element={<RequireRole allow="manager" />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
